import { AlertTriangle, House, RotateCw, SearchX } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';
import { Button } from '../ui/button.js';
import { StripeDivider } from './states.js';

/**
 * `import.meta.env.DEV`, written as that exact expression so Vite folds it to
 * a constant — a production bundle must not carry the branch that prints a
 * stack trace. The cast is only here because this package does not pull in
 * `vite/client`'s types; anywhere Vite is not doing the building it reads
 * `undefined` and the details stay hidden, which is the safe way round.
 */
const IS_DEV = (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;

/**
 * Whatever was thrown, reduced to something printable. Stacks already begin
 * with the message, so only a bare message needs adding.
 */
export function describeError(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof Error) return error.stack ?? `${error.name}: ${error.message}`;
  if (typeof error === 'string') return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

export interface ErrorPageProps {
  /** The large figure above the title — a status code, usually. */
  code?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Buttons. Laid out in a centred row that wraps. */
  actions?: ReactNode;
  /** Replaces the default glyph. Pass `null` for none. */
  icon?: ReactNode;
  /**
   * A stack trace, shown folded away beneath everything else. Only ever
   * rendered on a dev server — see `IS_DEV`.
   */
  details?: string | null;
  className?: string;
}

/**
 * The shell every error screen in the group is built from: one centred
 * column, the hazard stripe, and nothing that links anywhere except the
 * actions you pass.
 *
 * It centres itself vertically as well as horizontally, which takes a flex
 * parent with height to spare: the layouts' `main` is one, and the two
 * boundaries below wrap their own fallback in a window-high column because
 * they render with no layout around them. Where neither applies the
 * min-height keeps the column from collapsing onto the top edge.
 *
 * It is deliberately presentational — no router, no data, no hooks beyond
 * the translation of its own `details` toggle — because it has to survive
 * being rendered when the rest of the app has just failed.
 */
export function ErrorPage({
  code,
  title,
  description,
  actions,
  icon,
  details,
  className,
}: ErrorPageProps) {
  const { t } = useTranslation('common');
  const stack = IS_DEV ? details : null;

  return (
    <div
      className={cn(
        'mx-auto flex min-h-96 w-full max-w-xl flex-1 flex-col items-center justify-center px-4 py-20 text-center',
        className,
      )}
    >
      {icon === undefined ? (
        <div className="mb-6 flex size-14 items-center justify-center rounded-full bg-accent-soft text-accent-soft-text">
          <AlertTriangle className="size-6" aria-hidden="true" />
        </div>
      ) : (
        icon
      )}

      {code ? (
        <p className="font-display text-6xl font-bold leading-none tracking-tighter sm:text-7xl">
          {code}
        </p>
      ) : null}

      <StripeDivider className="my-6 w-20" />

      <h1 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">{title}</h1>
      {description ? (
        <p className="mt-3 text-balance text-sm text-text-muted sm:text-base">{description}</p>
      ) : null}

      {actions ? <div className="mt-8 flex flex-wrap justify-center gap-3">{actions}</div> : null}

      {stack ? (
        <details className="mt-10 w-full text-left">
          <summary className="cursor-pointer text-xs font-semibold text-text-subtle">
            {t('error.details')}
          </summary>
          <pre className="mt-2 max-h-72 overflow-auto rounded-card bg-surface-sunken p-4 text-left text-xs leading-relaxed text-text-muted">
            {stack}
          </pre>
        </details>
      ) : null}
    </div>
  );
}

export interface NotFoundProps {
  /** Overrides the shared copy — an app with its own voice passes these. */
  title?: string;
  description?: ReactNode;
  /** Where the button goes. `null` drops the button entirely. */
  homeHref?: string | null;
  homeLabel?: string;
  /** Rendered after the home button. */
  actions?: ReactNode;
  className?: string;
}

/**
 * The 404 page, shared by all three front-ends.
 *
 * The way home is a plain anchor rather than a router `Link` on purpose:
 * this renders inside a route boundary as often as it renders as a route,
 * and a hard navigation costs nothing on a dead URL while working even when
 * the router context above it is the thing that broke.
 */
export function NotFound({
  title,
  description,
  homeHref = '/',
  homeLabel,
  actions,
  className,
}: NotFoundProps) {
  const { t } = useTranslation('common');

  return (
    <ErrorPage
      code="404"
      icon={
        <div className="mb-6 flex size-14 items-center justify-center rounded-full bg-accent-soft text-accent-soft-text">
          <SearchX className="size-6" aria-hidden="true" />
        </div>
      }
      title={title ?? t('error.notFound.title')}
      description={description ?? t('error.notFound.body')}
      className={className}
      actions={
        <>
          {homeHref ? (
            <Button asChild>
              <a href={homeHref}>
                <House aria-hidden="true" />
                {homeLabel ?? t('error.home')}
              </a>
            </Button>
          ) : null}
          {actions}
        </>
      }
    />
  );
}

export interface ErrorFallbackProps {
  /** Whatever was caught. Only its stack is used, and only on a dev server. */
  error?: unknown;
  /** A route response status, shown as the figure. */
  status?: number;
  title?: string;
  description?: ReactNode;
  /**
   * Runs instead of a full page reload. A boundary reset only helps when the
   * cause was transient, so the default really does reload: it is the one
   * action that clears a half-initialised module graph.
   */
  onRetry?: () => void;
  /** `null` drops the home button. */
  homeHref?: string | null;
  className?: string;
}

/**
 * The "something went wrong" screen — what both boundaries below render, and
 * what the apps show in place of React Router's developer error page.
 */
export function ErrorFallback({
  error,
  status,
  title,
  description,
  onRetry,
  homeHref = '/',
  className,
}: ErrorFallbackProps) {
  const { t } = useTranslation('common');

  return (
    <ErrorPage
      code={status ? String(status) : undefined}
      title={title ?? t('error.title')}
      description={description ?? t('error.body')}
      details={describeError(error)}
      className={className}
      actions={
        <>
          <Button onClick={onRetry ?? (() => window.location.reload())}>
            <RotateCw aria-hidden="true" />
            {onRetry ? t('action.retry') : t('error.reload')}
          </Button>
          {homeHref ? (
            <Button variant="outline" asChild>
              <a href={homeHref}>{t('error.home')}</a>
            </Button>
          ) : null}
        </>
      }
    />
  );
}
